import React, {
  useState,
  useMemo,
  useRef,
  useCallback,
  useEffect,
} from 'react';
import type { PlayerBet as CrashPlayerBet } from '@platform/contracts';
import type { PlayerBet as AviatorPlayerBet } from '@platform/contracts';
import { createScoreboardStyles } from '../styles/ScoreBoard.styles';
import { skySnifferScoreboardOverrides } from '../styles/ScoreBoardSkySniffer.styles';
import { useTheme } from '@core/UI/context/ThemeContext';
import { resolveGameStoreHook } from '@core/gameengine/state/activeGameStore';
import { getRegisteredPlayerAvatarUrl } from '@core/Assets/assetManifestRegistry';
import { getAssetManifest, isSkySnifferGame } from '../utils/getAssetHelper';
import {
  buildGamePublicAssetUrl,
  resolveAssetFolderSlug,
} from '@core/utils/assetFolderSlug';
import { ProvablyFairInfoModal } from '@core/UI/overlayelements/ProvablyFairInfoModal';
import { useT, formatAmount, formatCurrencyUnit } from '@core/i18n';
import { isSkyxNewRoute } from '@core/utils/gameRouteSlugs';
import { mergeSkyxPfInfoModalChrome } from '@core/utils/skyxPfInfoModalStyles';
import { formatSkyxPlayerDisplayName } from '@core/utils/skyxPlayerDisplayName';
import { GlobalBus } from '@core/Bus/EventBus';

// --- Service Imports ---
import {
  LeaderboardService,
  LeaderboardEntry,
  PeriodFilter,
  PreviousRoundResponse,
} from '@Web/services/LeaderboardService';
import {
  ProvablyFairService,
  RoundHistoryResponse,
} from '@Web/services/provablyfairservice';

type PlayerBet = CrashPlayerBet | AviatorPlayerBet | any;

const MAX_SUMMARY_AVATARS = 3;

const ROUND_ACTIVE_AVATAR_PHASES = new Set([
  'WAITING',
  'BET_LOCK',
  'TAKEOFF',
  'FLYING',
  'REVEAL',
  'FLEW_AWAY',
]);

interface ScoreBoardProps {
  controller?: any; // Optional controller to determine which game state to use
}

// This interface matches what the Renshider function expects (UI View Model)
export interface TopPlayerStat {
  player: string;
  roundId: string; // Required for the history modal
  avatarDate: string; // Used for standard cards
  displayDateTime: string; // Used for Rounds tab (DD.MM.YY HH:mm)
  betAmount: number;
  winAmount: number;
  roundMultiplier: number;
  roundsPlayed: number;
  roundMax: number;
}

// --- STATE PERSISTENCE VARIABLE ---
// Defined outside the component so it survives re-mounts (resizing),
// but resets when the browser page is refreshed.
let savedTabState: 'all' | 'my' | 'top' | 'previous' = 'all';

interface PreviousRoundSnapshot {
  cacheKey: string;
  bets: PlayerBet[];
  crashX: number;
}

let cachedPreviousRound: PreviousRoundSnapshot | null = null;

const ROUND_END_PHASES = new Set(['FLEW_AWAY', 'REVEAL', 'CRASHED']);

function getPreviousRoundCacheKey(
  gameCode: number,
  tableNo: string | number
): string {
  return `${gameCode}-${tableNo}`;
}

function sortPreviousRoundBets(bets: PlayerBet[]): PlayerBet[] {
  return [...bets].sort((a, b) => {
    if (b.stake !== a.stake) {
      return b.stake - a.stake;
    }
    return (a.betNo || 0) - (b.betNo || 0);
  });
}

function transformPreviousRoundResponse(
  data: PreviousRoundResponse
): PlayerBet[] {
  const transformedPlayers: PlayerBet[] = data.players.map((p, index) => ({
    player: p.nickname,
    stake: p.bet / 100,
    betNo: 99000 + index,
    cashedAtX: p.cashoutMul || undefined,
    win: p.cashoutAmount > 0 ? p.cashoutAmount / 100 : undefined,
  }));

  return sortPreviousRoundBets(transformedPlayers);
}

function isCompletedPreviousRoundResponse(
  data: PreviousRoundResponse | null | undefined
): data is PreviousRoundResponse {
  return !!data && data.crashMultiplier > 0;
}

const TOP_FILTER_KEYS = ['X', 'Win', 'Rounds'] as const;
const TIME_FILTER_KEYS = ['Day', 'Month', 'Year'] as const;

function uniqueTopBetsByPlayer(
  bets: PlayerBet[],
  compare: (a: PlayerBet, b: PlayerBet) => number,
  limit = 3
): PlayerBet[] {
  const seenPlayers = new Set<string>();
  const uniqueBets: PlayerBet[] = [];

  for (const bet of [...bets].sort(compare)) {
    if (seenPlayers.has(bet.player)) continue;
    seenPlayers.add(bet.player);
    uniqueBets.push(bet);
    if (uniqueBets.length >= limit) break;
  }

  return uniqueBets;
}

export const ScoreBoard: React.FC<ScoreBoardProps> = ({ controller }) => {
  const { theme } = useTheme();

  // Determine which game state to use based on controller
  const gameSlug = controller?.getGameSlug?.() || 'crash';
  const isSkyxNew = isSkyxNewRoute(gameSlug);
  const t = useT(isSkyxNew ? 'skyx_new' : undefined);
  const assetFolder =
    controller?.getAssetBaseSlug?.() ??
    resolveAssetFolderSlug(gameSlug) ??
    gameSlug;
  const isSkySniffer = isSkySnifferGame(gameSlug);
  const useGameStore = resolveGameStoreHook(controller);
  const GAME_CODE = useGameStore((state: any) => state.gameId);
  const TABLE_NO = useGameStore((state: any) => state.tableId);
  const platformUIConfig = controller?.getPlatformUIConfig?.() || {};
  const uiProfile = platformUIConfig.profile;

  // Get appropriate assets based on game
  const assets = useMemo(() => {
    const gameManifest =
      controller?.getAssetManifest?.() ?? getAssetManifest(gameSlug);
    const resolveAvatar = (playerId: string) =>
      controller?.getPlayerAvatarUrl?.(playerId) ??
      getRegisteredPlayerAvatarUrl(gameSlug, playerId) ??
      '';

    if (gameManifest) {
      return {
        provablyFairIcon: gameManifest.images.provablyfairicon ?? '',
        chatIcon: gameManifest.images.chatIcon ?? '',
        shieldIcon: gameManifest.images.sheildIcon ?? '',
        getPlayerAvatarUrl: resolveAvatar,
      };
    }
    return {
      provablyFairIcon: '',
      chatIcon: '',
      shieldIcon: '',
      getPlayerAvatarUrl: resolveAvatar,
    };
  }, [controller, gameSlug]);

  const PROVABLY_FAIR_ICON = assets.provablyFairIcon;
  const CHAT_ICON = assets.chatIcon;
  const SHIELD_ICON = assets.shieldIcon;
  const getPlayerAvatarUrl = assets.getPlayerAvatarUrl;

  const deviceType = useGameStore((state: any) => state.deviceType);
  const isLandscape = useGameStore((state: any) => state.isLandscape);
  const openHistoryModal = useGameStore(
    (state: any) => state.openProvablyFairModal
  );

  const styles = useMemo(() => {
    const baseStyles = createScoreboardStyles(theme, deviceType, isLandscape);
    if (isSkySniffer) {
      const merged = { ...baseStyles };
      for (const key of Object.keys(skySnifferScoreboardOverrides)) {
        if (merged[key]) {
          merged[key] = {
            ...merged[key],
            ...skySnifferScoreboardOverrides[key],
          };
        } else {
          merged[key] = skySnifferScoreboardOverrides[key];
        }
      }
      return merged;
    }
    if (platformUIConfig.scoreboardStyleOverrides) {
      const merged = { ...baseStyles };
      for (const key of Object.keys(
        platformUIConfig.scoreboardStyleOverrides
      )) {
        if (merged[key]) {
          merged[key] = {
            ...merged[key],
            ...platformUIConfig.scoreboardStyleOverrides[key],
          };
        } else {
          merged[key] = platformUIConfig.scoreboardStyleOverrides[key];
        }
      }
      return merged;
    }
    return baseStyles;
  }, [theme, deviceType, isLandscape, gameSlug, platformUIConfig]);

  const provablyFairInfoStyles = useMemo(() => {
    const base = createScoreboardStyles(theme, deviceType, isLandscape);
    if (!isSkyxNew) return base;
    return mergeSkyxPfInfoModalChrome(base, deviceType);
  }, [theme, deviceType, isLandscape, isSkyxNew]);
  const usesCompactPurpleProfile = uiProfile === 'compactPurple';
  const usesCompactPurpleMobileSummary =
    usesCompactPurpleProfile && deviceType === 'mobile';
  const formatBetCount = (active: number, total: number) =>
    `${active}/${total}`;

  const renderBetCountDisplay = (active: number, total: number) => {
    if (isSkyxNew) {
      return (
        <span style={styles.betCount}>
          <span style={styles.betCountActive}>{active}</span>
          <span style={styles.betCountSuffix}>/{total}</span>
        </span>
      );
    }
    return <span style={styles.betCount}>{formatBetCount(active, total)}</span>;
  };

  // Initialize state from the module-level variable
  const [activeTab, setActiveTab] = useState<'all' | 'my' | 'top' | 'previous'>(
    savedTabState
  );

  // Keep the module-level variable in sync when tab changes
  useEffect(() => {
    savedTabState = activeTab;
  }, [activeTab]);

  // Leaderboard & Previous State
  const [topFilter, setTopFilter] = useState<'X' | 'Win' | 'Rounds'>('X');
  const [timeFilter, setTimeFilter] = useState<'Day' | 'Month' | 'Year'>('Day');

  const [leaderboardData, setLeaderboardData] = useState<TopPlayerStat[]>([]);
  const [isLoadingLeaderboard, setIsLoadingLeaderboard] = useState(false);

  // Previous Round State
  const initialPreviousCache =
    cachedPreviousRound &&
    cachedPreviousRound.cacheKey ===
      getPreviousRoundCacheKey(GAME_CODE, TABLE_NO)
      ? cachedPreviousRound
      : null;
  const [previousRoundBets, setPreviousRoundBets] = useState<PlayerBet[]>(
    () => initialPreviousCache?.bets ?? []
  );
  const [previousRoundCrashX, setPreviousRoundCrashX] = useState<number>(
    () => initialPreviousCache?.crashX ?? 0
  );
  const [isLoadingPrevious, setIsLoadingPrevious] = useState(false);
  const hasPreviousRoundCache =
    previousRoundBets.length > 0 || previousRoundCrashX > 0;

  const [isExpanded, setIsExpanded] = useState(false);
  // Local state for the "General Fairness Explanation" modal (Footer link)
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProvablyFairActive, setIsProvablyFairActive] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const betListRef = useRef<HTMLDivElement>(null);

  // --- State Selector ---
  const stateSelector = (s: any) => ({
    allBets: s.allBets || [],
    currency: s.currency || 'EUR',
    activePlayersCount: s.activePlayersCount || 0,
    activeBetsCount: s.activeBetsCount || 0,
    totalBetsCount: s.totalBetsCount || 0,
    totalCashoutAmount: s.totalCashoutAmount || 0,
    assetManager: s.assetManager,
    phase: s.phase,
    crashedAtX: s.crashedAtX ?? null,
  });

  const {
    allBets,
    currency,
    activePlayersCount,
    activeBetsCount,
    totalBetsCount,
    totalCashoutAmount,
    assetManager,
    phase,
    crashedAtX,
  } = useGameStore(stateSelector);
  const previousPhaseRef = useRef(phase);
  const lastPreviousSnapshotRef = useRef<{
    crashX: number;
    betCount: number;
  } | null>(null);

  const pfInfoHeaderIconUrl = useMemo(
    () =>
      platformUIConfig.resolveProvablyFairInfoHeaderIconUrl?.(
        assetManager?.baseUrl
      ),
    [platformUIConfig, assetManager?.baseUrl]
  );

  const formatNumber = useCallback(
    (value: number | undefined | null) => formatAmount(value, currency),
    [currency]
  );

  // --- MEMOIZED SORTED BETS FOR "ALL" TAB ---
  // Sorts by Stake (Descending) -> Then by BetNo/Time (Ascending)
  const sortedAllBets = useMemo(() => {
    // Create a copy to avoid mutating the store array directly
    return [...allBets].sort((a: PlayerBet, b: PlayerBet) => {
      // 1. Primary: Sort by Stake (Higher first)
      if (b.stake !== a.stake) {
        return b.stake - a.stake;
      }
      // 2. Secondary: Sort by Time (First placed first)
      // Assuming betNo increments with time
      return (a.betNo || 0) - (b.betNo || 0);
    });
  }, [allBets]);

  const applyPreviousRoundSnapshot = useCallback(
    (bets: PlayerBet[], crashX: number) => {
      const cacheKey = getPreviousRoundCacheKey(GAME_CODE, TABLE_NO);
      const snapshot: PreviousRoundSnapshot = {
        cacheKey,
        bets,
        crashX,
      };
      cachedPreviousRound = snapshot;
      setPreviousRoundBets(bets);
      setPreviousRoundCrashX(crashX);
    },
    [GAME_CODE, TABLE_NO]
  );

  const fetchPreviousRound = useCallback(
    async (options?: { showLoadingWhenEmpty?: boolean }) => {
      const showLoadingWhenEmpty = options?.showLoadingWhenEmpty ?? true;
      const cacheKey = getPreviousRoundCacheKey(GAME_CODE, TABLE_NO);
      const hasCachedData =
        cachedPreviousRound?.cacheKey === cacheKey &&
        (cachedPreviousRound.bets.length > 0 || cachedPreviousRound.crashX > 0);

      if (showLoadingWhenEmpty && !hasCachedData) {
        setIsLoadingPrevious(true);
      }

      try {
        const data = await LeaderboardService.getInstance().getPreviousRound(
          GAME_CODE,
          TABLE_NO
        );

        if (isCompletedPreviousRoundResponse(data)) {
          applyPreviousRoundSnapshot(
            transformPreviousRoundResponse(data),
            data.crashMultiplier
          );
        } else if (!hasCachedData) {
          cachedPreviousRound = null;
          setPreviousRoundCrashX(0);
          setPreviousRoundBets([]);
        }
      } finally {
        setIsLoadingPrevious(false);
      }
    },
    [GAME_CODE, TABLE_NO, applyPreviousRoundSnapshot]
  );

  // --- DATA FETCHING FOR LEADERBOARD & PREVIOUS ---
  useEffect(() => {
    const service = LeaderboardService.getInstance();

    // Fetch Top Stats
    if (activeTab === 'top') {
      const fetchLeaderboard = async () => {
        setIsLoadingLeaderboard(true);
        const period: PeriodFilter = timeFilter.toLowerCase() as PeriodFilter;

        let data: LeaderboardEntry[] = [];

        if (topFilter === 'X') {
          data = await service.getTopMultiplier(period, GAME_CODE, TABLE_NO);
        } else if (topFilter === 'Win') {
          data = await service.getTopWin(period, GAME_CODE, TABLE_NO);
        } else if (topFilter === 'Rounds') {
          data = await service.getTopRounds(period, GAME_CODE, TABLE_NO);
        }

        const transformed: TopPlayerStat[] = data.map(entry => {
          const d = new Date(entry.date);
          const day = d.getDate().toString().padStart(2, '0');
          const month = (d.getMonth() + 1).toString().padStart(2, '0');
          const year = d.getFullYear().toString().slice(-2);
          const hours = d.getHours().toString().padStart(2, '0');
          const minutes = d.getMinutes().toString().padStart(2, '0');

          const formattedDate = `${day}.${month}.${year}`;
          const formattedDateTime = `${day}.${month}.${year} ${hours}:${minutes}`;

          const playerDisplay = entry.nickname
            ? entry.nickname
            : `Round ${entry.roundNo}`;
          const effectiveMultiplier =
            entry.resultMultiplier || entry.multiplier || 0;
          const effectiveMax =
            entry.roundMaxMultiplier || entry.multiplier || 0;

          return {
            player: playerDisplay,
            roundId: entry.roundNo?.toString() || '',
            avatarDate: formattedDate,
            displayDateTime: formattedDateTime,
            betAmount: entry.bet || 0,
            winAmount: entry.win || 0,
            roundMultiplier: effectiveMultiplier,
            roundsPlayed: 0,
            roundMax: effectiveMax,
          };
        });

        setLeaderboardData(transformed);
        setIsLoadingLeaderboard(false);
      };
      fetchLeaderboard();
    }

    if (activeTab === 'previous') {
      void fetchPreviousRound();
    }
  }, [
    activeTab,
    topFilter,
    timeFilter,
    GAME_CODE,
    TABLE_NO,
    fetchPreviousRound,
  ]);

  useEffect(() => {
    const previousPhase = previousPhaseRef.current;

    if (
      ROUND_END_PHASES.has(phase) &&
      crashedAtX != null &&
      crashedAtX > 0 &&
      allBets.length > 0
    ) {
      const lastSnapshot = lastPreviousSnapshotRef.current;
      if (
        lastSnapshot?.crashX !== crashedAtX ||
        lastSnapshot?.betCount !== allBets.length
      ) {
        applyPreviousRoundSnapshot(sortPreviousRoundBets(allBets), crashedAtX);
        lastPreviousSnapshotRef.current = {
          crashX: crashedAtX,
          betCount: allBets.length,
        };
      }
    }

    if (phase === 'WAITING') {
      lastPreviousSnapshotRef.current = null;
    }

    if (
      phase === 'WAITING' &&
      ROUND_END_PHASES.has(previousPhase) &&
      activeTab === 'previous'
    ) {
      void fetchPreviousRound({ showLoadingWhenEmpty: false });
    }

    previousPhaseRef.current = phase;
  }, [
    phase,
    crashedAtX,
    allBets,
    activeTab,
    applyPreviousRoundSnapshot,
    fetchPreviousRound,
  ]);

  // Handler for the "General Fairness" text link at the footer
  const handleProvablyFairClick = useCallback(() => {
    setIsModalOpen(true);
    setIsProvablyFairActive(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setIsProvablyFairActive(false);
  }, []);

  // Handler for the Shield Icon (Specific Round Detail)
  const handleShieldClick = async (
    e: React.MouseEvent,
    stat: TopPlayerStat
  ) => {
    e.stopPropagation();

    const roundIdNumber = Number(stat.roundId);

    if (openHistoryModal && !isNaN(roundIdNumber)) {
      // skyx_new uses the Provably Fair audit + verify APIs; the modal fetches
      // the real round record itself, so just open it with the round id.
      if (isSkyxNewRoute(gameSlug)) {
        openHistoryModal({
          roundId: roundIdNumber,
          bust: stat.roundMultiplier,
          serverSeed: '',
          serverSeedHash: '',
          timeStamp: 0,
        } as any);
        return;
      }

      const pfService = ProvablyFairService.getInstance();

      try {
        const roundData: RoundHistoryResponse | null =
          await pfService.getRoundHistory(TABLE_NO, roundIdNumber);

        if (roundData && roundData.serverSeed) {
          // Transform the fetched data into the expected HistoryItem format for the modal
          openHistoryModal({
            roundId: roundData.roundNo.toString(),
            bust: roundData.crashX,
            serverSeed: roundData.serverSeed,
            clientSeed: roundData.clientSeed ?? 'N/A',
            serverSeedHash: roundData.serverSeedHash ?? 'N/A',
            timeStamp: roundData.endedAt,
          } as any);
        } else {
          // Fallback if data fetch fails, using existing stat but logging error
          console.error(
            `Failed to fetch detailed Provably Fair data for round ${stat.roundId}`
          );
          openHistoryModal({
            roundId: stat.roundId,
            bust: stat.roundMultiplier,
            serverSeed: 'Error/Not Found',
            clientSeed: 'Error/Not Found',
            serverSeedHash: 'Error/Not Found',
            timeStamp: 0,
          } as any);
        }
      } catch (error) {
        console.error(
          `Error fetching Provably Fair data for round ${stat.roundId}:`,
          error
        );
        openHistoryModal({
          roundId: stat.roundId,
          bust: stat.roundMultiplier,
          serverSeed: 'Error/Network',
          clientSeed: 'Error/Network',
          serverSeedHash: 'Error/Network',
          timeStamp: 0,
        } as any);
      }
    }
  };

  // --- Helper functions ---
  const getMultiplierColor = (x: number) => {
    if (isSkySniffer || usesCompactPurpleProfile) {
      // Subdued colors for Sky Sniffer
      if (x >= 10) return 'rgba(160, 120, 200, 0.8)';
      if (x >= 2) return 'rgba(100, 140, 190, 0.8)';
      return 'rgba(80, 150, 200, 0.6)';
    }
    if (x >= 10) return 'rgb(192, 23, 180)';
    if (x >= 2) return 'rgb(145, 62, 248)';
    return 'rgb(52, 180, 255)';
  };

  const getAvatarColor = (playerId: string) => {
    const colors = [
      '#e91e63',
      '#2196f3',
      '#ff9800',
      '#4caf50',
      'rgb(145, 62, 248)',
      '#ff5722',
      '#607d8b',
      '#795548',
    ];
    const hash = playerId.split('').reduce((a, b) => {
      a = (a << 5) - a + b.charCodeAt(0);
      return a & a;
    }, 0);
    return colors[Math.abs(hash) % colors.length];
  };

  const getPlayerDisplayName = (player: string) => {
    if (player.startsWith('Round')) return player;
    if (isSkyxNew) return formatSkyxPlayerDisplayName(player);
    if (player.length <= 4) return player;
    return `${player.charAt(0)}***${player.charAt(player.length - 1)}`;
  };

  const [avatarRevision, setAvatarRevision] = useState(0);

  useEffect(() => {
    if (!isSkyxNew) return;
    return GlobalBus.on('player:avatar-changed', () => {
      setAvatarRevision(value => value + 1);
    });
  }, [isSkyxNew]);

  const getResolvedAvatarUrl = useCallback(
    (playerId: string): string => {
      const avatarPath = getPlayerAvatarUrl(playerId);
      if (!avatarPath) return '';
      if (/^(https?:\/\/|data:image\/|blob:)/i.test(avatarPath)) {
        return avatarPath;
      }
      return (
        controller?.getAssetUrl?.(avatarPath) ??
        buildGamePublicAssetUrl(assetFolder, avatarPath, assetManager?.baseUrl)
      );
    },
    [
      assetFolder,
      assetManager?.baseUrl,
      avatarRevision,
      controller,
      getPlayerAvatarUrl,
    ]
  );

  const applyFallbackAvatar = useCallback(
    (
      event: React.SyntheticEvent<HTMLImageElement>,
      fallbackPlayerId: string
    ) => {
      if (isSkyxNew) {
        const retryUrl = getResolvedAvatarUrl(fallbackPlayerId);
        if (retryUrl && event.currentTarget.src !== retryUrl) {
          event.currentTarget.src = retryUrl;
        }
        return;
      }

      const fallbackHash = fallbackPlayerId.split('').reduce((acc, char) => {
        return char.charCodeAt(0) + ((acc << 5) - acc);
      }, 0);
      const fallbackIndex = (Math.abs(fallbackHash) % 15) + 1;
      const fallbackUrl = buildGamePublicAssetUrl(
        'skyx_new',
        `images/avatars/avatar${fallbackIndex}.png`,
        assetManager?.baseUrl
      );
      if (fallbackUrl && event.currentTarget.src !== fallbackUrl) {
        event.currentTarget.src = fallbackUrl;
      }
    },
    [assetManager?.baseUrl, getResolvedAvatarUrl, isSkyxNew]
  );

  const formatTopStatChatMessage = useCallback(
    (stat: TopPlayerStat, playerName: string) => {
      const betLabel = t('ui.control_panel.bet_panel.bet_currency', {
        currency: formatCurrencyUnit(currency),
      });
      const winLabel = t('ui.hud.win_currency', {
        currency: formatCurrencyUnit(currency),
      });
      return [
        playerName,
        `${betLabel}: ${formatNumber(stat.betAmount)}`,
        `${winLabel}: ${formatNumber(stat.winAmount)}`,
        `${t('ui.scoreboard.result')}: ${formatNumber(stat.roundMultiplier)}x`,
        `${t('ui.scoreboard.round_max')}: ${formatNumber(stat.roundMax)}x`,
      ].join(' | ');
    },
    [currency, formatNumber, t]
  );

  const handleChatClick = useCallback(
    (e: React.MouseEvent, stat: TopPlayerStat) => {
      e.stopPropagation();
      const displayName = getPlayerDisplayName(stat.player);

      if (isSkyxNew) {
        GlobalBus.emit('skyx_new:open-chat-share', {
          player: displayName,
          playerId: stat.player,
          bet: stat.betAmount,
          win: stat.winAmount,
          cashout: stat.roundMultiplier,
          round: stat.roundMax,
          roundId: stat.roundId,
          currency,
          avatarUrl: getResolvedAvatarUrl(stat.player) || undefined,
        });
        return;
      }

      const readableMessage = formatTopStatChatMessage(stat, displayName);

      void (async () => {
        try {
          await navigator.clipboard.writeText(readableMessage);
        } catch (err) {
          console.warn(
            '[ScoreBoard] Failed to copy row data to clipboard',
            err
          );
        }

        GlobalBus.emit('chat:send_message', {
          text: readableMessage,
          open: true,
        });
      })();
    },
    [
      currency,
      formatTopStatChatMessage,
      getPlayerDisplayName,
      getResolvedAvatarUrl,
      isSkyxNew,
    ]
  );

  const renderBetRow = (bet: PlayerBet, index: number) => {
    const isPreviousWinner =
      activeTab === 'previous' &&
      bet.cashedAtX &&
      bet.cashedAtX < previousRoundCrashX;

    const isCurrentRoundWinner =
      activeTab !== 'previous' && !!bet.cashedAtX && bet.cashedAtX > 1;

    const isWinner = isPreviousWinner || isCurrentRoundWinner;
    const multiplierForColor = bet.cashedAtX || 1.0;

    const rowStyle: React.CSSProperties = {
      ...styles.betRow,
      ...(isWinner ? styles.betRowWinner : {}),
    };
    const multiplierColor = usesCompactPurpleProfile
      ? '#B347FF'
      : isSkySniffer
        ? '#ffffff'
        : isWinner
          ? getMultiplierColor(multiplierForColor)
          : '#a3a3a3';

    const winAmountColor = usesCompactPurpleProfile
      ? '#B347FF'
      : isSkySniffer
        ? '#00d4ff'
        : isWinner
          ? '#bbbfc5'
          : '#a3a3a3';
    const betAmountColor =
      isSkySniffer || usesCompactPurpleProfile
        ? '#ffffff' // your required color
        : '#ffffff'; // default

    const displayName = getPlayerDisplayName(bet.player);
    const avatarUrl = getResolvedAvatarUrl(bet.player);

    return (
      <div
        key={`${bet.player}-${bet.betNo}-${index}`}
        style={rowStyle}
        className={usesCompactPurpleProfile ? 'skyx-scoreboard-row' : undefined}
      >
        <div
          style={{
            ...styles.betRowCell,
            minWidth: isSkyxNew ? '128px' : '92px',
            width: isSkyxNew ? 'auto' : undefined,
            flex: isSkyxNew ? '0 0 auto' : undefined,
            marginLeft: 0,
            display: 'flex',
            alignItems: 'center',
            padding: '0px',
            overflow: isSkyxNew ? 'visible' : undefined,
          }}
        >
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt="avatar"
              onError={event => applyFallbackAvatar(event, bet.player)}
              style={{
                width: '24px',
                height: '24px',
                marginRight: '8px',
                borderRadius: '50%',
              }}
            />
          ) : (
            <div
              style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                background: getAvatarColor(bet.player),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                color: 'white',
                fontWeight: 'bold',
                marginRight: '8px',
              }}
            >
              {' '}
              {bet.player.charAt(0).toUpperCase()}{' '}
            </div>
          )}
          <div style={styles.username}>{displayName}</div>
        </div>
        <div
          style={{
            ...styles.betRowCell,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            minWidth: '72px',
            color: betAmountColor,
          }}
        >
          {' '}
          {/* UPDATED: Use formatNumber */}
          {formatNumber(bet.stake)}{' '}
        </div>
        <div
          style={{
            ...styles.betRowCell,
            color: multiplierColor,
            fontWeight: 500,
          }}
        >
          {bet.cashedAtX
            ? `${formatNumber(bet.cashedAtX)}x` // UPDATED: Use formatNumber
            : activeTab === 'previous' && previousRoundCrashX > 1.0
              ? ''
              : ''}
        </div>
        <div
          style={{
            ...styles.betRowCell,
            color: winAmountColor,
            fontWeight: 500,
          }}
        >
          {/* UPDATED: Use formatNumber */}
          {bet.win ? formatNumber(bet.win) : '0.00'}
        </div>
      </div>
    );
  };

  const renderTopBetRow = (stat: TopPlayerStat, index: number) => {
    // --- SPECIFIC RENDER FOR ROUNDS TAB ---
    if (topFilter === 'Rounds') {
      const shieldIconUrl =
        controller?.getAssetUrl?.(SHIELD_ICON) ??
        buildGamePublicAssetUrl(
          assetFolder,
          SHIELD_ICON,
          assetManager?.baseUrl
        );

      // UPDATED: Use formatNumber
      const multiplierStr = formatNumber(stat.roundMultiplier);
      const multiplierColor = usesCompactPurpleProfile
        ? '#B347FF'
        : getMultiplierColor(stat.roundMultiplier);

      return (
        <div
          key={stat.player + index}
          style={styles.roundsRow}
          className={
            usesCompactPurpleProfile ? 'skyx-scoreboard-row' : undefined
          }
        >
          <div style={styles.roundsDateCell}>{stat.displayDateTime}</div>

          {/* Center X in Row */}
          <div style={{ ...styles.roundsMultiplier, color: multiplierColor }}>
            {multiplierStr}x
          </div>

          {/* Right Aligned Shield */}
          <div style={styles.roundsRightContainer}>
            <div
              style={{ ...styles.roundsShieldContainer, cursor: 'pointer' }}
              className={
                usesCompactPurpleProfile
                  ? 'skyx-scoreboard-icon-btn'
                  : undefined
              }
              onClick={e => handleShieldClick(e, stat)}
            >
              <img
                src={shieldIconUrl}
                alt="verified"
                style={{
                  ...styles.topPlayerIcon,
                  ...(usesCompactPurpleProfile
                    ? { width: '27px', height: '27px' }
                    : {}),
                }}
              />
            </div>
          </div>
        </div>
      );
    }

    // --- STANDARD RENDER FOR X AND WIN TABS ---
    const rowStyle: React.CSSProperties = {
      ...styles.betRow,
      height: 'auto',
      flexDirection: 'column',
      padding: usesCompactPurpleProfile ? '8px 0' : '2px 0',
      minHeight: '80px',
      ...(usesCompactPurpleProfile
        ? {}
        : {
            backgroundColor: '#121212',
            border: 'none',
          }),
    };
    const avatarUrl = getResolvedAvatarUrl(stat.player);
    const displayName = getPlayerDisplayName(stat.player);

    const chatIconUrl = controller?.getAssetUrl?.(CHAT_ICON) ?? '';
    const shieldIconUrl = controller?.getAssetUrl?.(SHIELD_ICON) ?? '';

    // Stat values are the same regardless of X or Win tab, only the labels change
    const mainStatLabel = t('ui.scoreboard.result');
    const secondaryStatLabel = t('ui.scoreboard.round_max');
    const mainStatValue = formatNumber(stat.roundMultiplier) + 'x';
    const secondaryStatValue = formatNumber(stat.roundMax) + 'x';

    return (
      <div
        key={stat.player + index}
        style={rowStyle}
        className={usesCompactPurpleProfile ? 'skyx-scoreboard-row' : undefined}
      >
        {/* TOP ROW: Player Info & Icons */}
        <div style={styles.topPlayerInfo}>
          <div style={styles.topPlayerDetails}>
            {avatarUrl && !stat.player.startsWith('Round') ? (
              <img
                src={avatarUrl}
                alt="avatar"
                onError={event => applyFallbackAvatar(event, stat.player)}
                style={{
                  width: usesCompactPurpleProfile ? '30px' : '32px',
                  height: usesCompactPurpleProfile ? '30px' : '32px',
                  marginRight: '8px',
                  borderRadius: '50%',
                }}
              />
            ) : (
              <div
                style={{
                  width: usesCompactPurpleProfile ? '30px' : '32px',
                  height: usesCompactPurpleProfile ? '30px' : '32px',
                  borderRadius: '50%',
                  background: getAvatarColor(stat.player),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: usesCompactPurpleProfile ? '10px' : '12px',
                  color: 'white',
                  fontWeight: '500',
                  marginRight: '8px',
                }}
              >
                {stat.player.startsWith('Round')
                  ? 'R'
                  : stat.player.charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <div style={styles.topPlayerName}>{displayName}</div>
              <div
                style={{
                  ...styles.topPlayerDate,
                  marginTop: usesCompactPurpleProfile ? '3px' : undefined,
                }}
              >
                {stat.avatarDate}
              </div>
            </div>
          </div>

          {!stat.player.startsWith('Round') && (
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              {chatIconUrl && (
                <div
                  style={{ ...styles.iconWrapper, cursor: 'pointer' }}
                  className={
                    usesCompactPurpleProfile
                      ? 'skyx-scoreboard-icon-btn'
                      : undefined
                  }
                  role="button"
                  tabIndex={0}
                  aria-label={t('ui.chat.send')}
                  onClick={e => void handleChatClick(e, stat)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      void handleChatClick(
                        e as unknown as React.MouseEvent,
                        stat
                      );
                    }
                  }}
                >
                  <img src={chatIconUrl} alt="" style={styles.topPlayerIcon} />
                </div>
              )}
              {shieldIconUrl && (
                <div
                  style={{ ...styles.iconWrapper, cursor: 'pointer' }}
                  className={
                    usesCompactPurpleProfile
                      ? 'skyx-scoreboard-icon-btn'
                      : undefined
                  }
                  onClick={e => handleShieldClick(e, stat)}
                >
                  <img
                    src={shieldIconUrl}
                    alt="Shield"
                    style={{
                      ...styles.topPlayerIcon,
                      ...(usesCompactPurpleProfile
                        ? { width: '27px', height: '27px' }
                        : {}),
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* BOTTOM ROW: Stats (Currency Group and Multiplier Group) */}
        <div style={styles.topStatsContainer}>
          {/* Currency Group (Left) */}
          <div style={styles.topCurrencyGroup}>
            <div style={styles.topLeftAlignCol}>
              <span style={styles.topCurrencyLabel}>
                {t('ui.control_panel.bet_panel.bet_currency', {
                  currency: formatCurrencyUnit(currency),
                })}
              </span>
              <span style={styles.topCurrencyLabel}>
                {t('ui.hud.win_currency', {
                  currency: formatCurrencyUnit(currency),
                })}
              </span>
            </div>
            <div style={styles.topRightAlignCol}>
              <span style={styles.topCurrencyValue}>
                {/* UPDATED: Use formatNumber */}
                {formatNumber(stat.betAmount)}
              </span>
              <span style={styles.topCurrencyValue}>
                {/* UPDATED: Use formatNumber */}
                {formatNumber(stat.winAmount)}
              </span>
            </div>
          </div>

          {/* Multiplier Group (Right) */}
          <div style={styles.topStatGroup}>
            <div style={styles.topLeftAlignCol}>
              <span style={styles.topStatLabel}>{mainStatLabel}</span>
              <span style={styles.topStatLabel}>{secondaryStatLabel}</span>
            </div>
            <div style={styles.topRightAlignCol}>
              <span style={styles.topStatValue}>{mainStatValue}</span>
              <span style={styles.topStatValue}>{secondaryStatValue}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Define betsToShow
  const topSummaryPlayers = useMemo(() => {
    const activeBets = allBets.filter(
      (bet: PlayerBet) => bet.cashedAtX == null
    );

    let players: PlayerBet[];
    if (ROUND_ACTIVE_AVATAR_PHASES.has(phase)) {
      players = uniqueTopBetsByPlayer(
        activeBets,
        (a, b) => b.stake - a.stake,
        MAX_SUMMARY_AVATARS
      );
    } else {
      players = uniqueTopBetsByPlayer(
        allBets.filter((bet: PlayerBet) => bet.win && bet.win > 0),
        (a, b) => (b.win ?? 0) - (a.win ?? 0),
        MAX_SUMMARY_AVATARS
      );
    }

    const avatarCount =
      activePlayersCount > 0
        ? Math.min(activePlayersCount, MAX_SUMMARY_AVATARS)
        : players.length;

    return players.slice(0, avatarCount);
  }, [allBets, phase, activePlayersCount]);

  let betsToShow: PlayerBet[] | TopPlayerStat[];
  switch (activeTab) {
    case 'top':
      betsToShow = leaderboardData;
      break;
    case 'previous':
      betsToShow = previousRoundBets;
      break;
    default:
      // Updated to use the Memoized sorted list
      betsToShow = sortedAllBets;
      break;
  }

  // --- UPDATED PROGRESS BAR LOGIC ---
  // 1. Initialize at 0
  let progressBarWidth = 0;

  // 2. Only calculate during the FLYING phase
  if (
    (phase === 'FLYING' || phase === 'REVEAL' || phase === 'FLEW_AWAY') &&
    totalBetsCount > 0
  ) {
    // 3. Calculate percentage of ACTIVE players (e.g., 90/100 = 90%)
    const activePercentage = (activeBetsCount / totalBetsCount) * 100;

    // 4. Invert it: We want the bar to represent people who have CASHED OUT/LEFT.
    // Start: 100 Active / 100 Total = 100%. Bar = 100 - 100 = 0%.
    // Mid: 90 Active / 100 Total = 90%. Bar = 100 - 90 = 10%.
    progressBarWidth = 100 - activePercentage;
  }
  // In Lobby or Finished phases, progressBarWidth remains 0.

  const skyxTabClassName = (isInactive: boolean) =>
    [
      usesCompactPurpleProfile ? 'skyx-scoreboard-tab' : null,
      isInactive ? 'inactive-tab-hover' : null,
    ]
      .filter(Boolean)
      .join(' ') || undefined;

  const containerStyle =
    deviceType === 'mobile'
      ? isExpanded
        ? styles.leftPanelMobileExpanded
        : styles.leftPanelMobile
      : deviceType === 'tablet'
        ? styles.leftPanelTablet
        : styles.leftPanelDesktop;

  const provablyFairIconUrl =
    controller?.getAssetUrl?.(PROVABLY_FAIR_ICON) ??
    buildGamePublicAssetUrl(
      assetFolder,
      PROVABLY_FAIR_ICON,
      assetManager?.baseUrl
    );

  return (
    <div ref={containerRef} style={containerStyle}>
      {/* Add Animation Keyframes specifically for the loader AND HOVER EFFECTS */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          /* RED HOVER EFFECT */
          .inactive-tab-hover:hover {
              color: ${isSkySniffer ? 'rgba(255,255,255,0.6)' : usesCompactPurpleProfile ? 'inherit' : '#d0021b'} !important;
          }

          ${
            usesCompactPurpleProfile
              ? `
          body.skyx-new-theme .skyx-scoreboard-tab,
          body.skyx-new-theme .bet-list-scrollbar {
            font-family: 'Roboto', sans-serif !important;
          }
          .inactive-tab-hover:hover {
            filter: brightness(1.15);
          }
          .inactive-tab-hover:active {
            transform: translateY(1px);
          }
          .skyx-scoreboard-icon-btn {
            transition: filter 0.12s ease, transform 0.08s ease, box-shadow 0.08s ease;
          }
          .skyx-scoreboard-icon-btn:hover {
            filter: brightness(1.08);
          }
          .skyx-scoreboard-icon-btn:active {
            transform: translateY(1px);
            filter: brightness(0.96);
          }
          body.skyx-new-theme .bet-list-scrollbar {
            scrollbar-color: #3c2193 #1F0A4D !important;
            border-radius: 0 !important;
          }
          body.skyx-new-theme .bet-list-scrollbar::-webkit-scrollbar {
            width: 6px;
          }
          body.skyx-new-theme .bet-list-scrollbar::-webkit-scrollbar-track {
            background: #1F0A4D !important;
          }
          body.skyx-new-theme .bet-list-scrollbar::-webkit-scrollbar-thumb {
            background: #3c2193 !important;
            border-radius: 3px;
          }
          body.skyx-new-theme .bet-list-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #4d2bb8 !important;
          }
          body.skyx-new-theme .bet-list-scrollbar::-webkit-scrollbar-button {
            display: none;
            width: 0;
            height: 0;
          }
          `
              : ''
          }
        `}
      </style>

      {/* EXPAND/COLLAPSE TOGGLE BUTTON for Mobile/Tablet */}
      {(deviceType === 'mobile' || deviceType === 'tablet') && !isSkyxNew && (
        <div
          onClick={() => setIsExpanded(!isExpanded)}
          style={styles.expandCollapseButton}
        >
          {/* Down chevron */}
          <svg
            width="16"
            height="10"
            viewBox="0 0 16 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2 2L8 8L14 2"
              stroke="white"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {/* Up chevron */}
          <svg
            width="16"
            height="10"
            viewBox="0 0 16 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2 8L8 2L14 8"
              stroke="white"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      )}

      <div style={styles.leftPanelTabs}>
        <button
          onClick={() => setActiveTab('all')}
          className={skyxTabClassName(activeTab !== 'all')}
          style={
            activeTab === 'all'
              ? styles.leftPanelTabActive
              : styles.leftPanelTab
          }
        >
          {t('ui.scoreboard.tab_all_bets')}
        </button>
        <button
          onClick={() => setActiveTab('previous')}
          className={skyxTabClassName(activeTab !== 'previous')}
          style={
            activeTab === 'previous'
              ? styles.leftPanelTabActive
              : styles.leftPanelTab
          }
        >
          {t('ui.scoreboard.tab_previous')}
        </button>
        <button
          onClick={() => {
            setActiveTab('top');
            setTopFilter('X');
            setTimeFilter('Day');
          }}
          className={skyxTabClassName(activeTab !== 'top')}
          style={
            activeTab === 'top'
              ? styles.leftPanelTabActive
              : styles.leftPanelTab
          }
        >
          {t('ui.scoreboard.tab_top')}
        </button>
      </div>

      {/* --- MAIN DATA CONTAINER --- */}
      <div
        style={styles.dataListContainer}
        className={
          usesCompactPurpleProfile ? 'skyx-scoreboard-datalist' : undefined
        }
      >
        {activeTab === 'previous' ? (
          (!isLoadingPrevious || hasPreviousRoundCache) && (
            <div style={styles.previousResultContainer}>
              <p style={styles.previousResultLabel}>
                {t('ui.game_info.round_result')}
              </p>
              <h3
                style={{
                  ...styles.previousResultX,
                  color: isSkyxNew
                    ? '#ffffff'
                    : getMultiplierColor(previousRoundCrashX),
                }}
              >
                {previousRoundCrashX > 0
                  ? formatNumber(previousRoundCrashX) + 'x' // UPDATED: Use formatNumber
                  : '-'}
              </h3>
            </div>
          )
        ) : activeTab === 'top' ? (
          // Container styles
          <div
            style={styles.topFiltersContainer}
            className={
              usesCompactPurpleProfile
                ? 'skyx-scoreboard-top-filters'
                : undefined
            }
          >
            <div style={styles.topFilterButtonGroup}>
              {TOP_FILTER_KEYS.map(filter => (
                <button
                  key={filter}
                  onClick={() => setTopFilter(filter)}
                  // ADDED: Red Hover Conditional Class
                  className={skyxTabClassName(topFilter !== filter)}
                  style={
                    topFilter === filter
                      ? styles.topFilterButtonActive
                      : styles.topFilterButton
                  }
                >
                  {t(
                    filter === 'X'
                      ? 'ui.scoreboard.filter_x'
                      : filter === 'Win'
                        ? 'ui.scoreboard.filter_win'
                        : 'ui.scoreboard.filter_rounds'
                  )}
                </button>
              ))}
            </div>
            <div style={styles.topFilterButtonGroup}>
              {TIME_FILTER_KEYS.map(filter => (
                <button
                  key={filter}
                  onClick={() => setTimeFilter(filter)}
                  // ADDED: Red Hover Conditional Class
                  className={skyxTabClassName(timeFilter !== filter)}
                  style={
                    timeFilter === filter
                      ? styles.topFilterButtonActive
                      : styles.topFilterButton
                  }
                >
                  {t(
                    filter === 'Day'
                      ? 'ui.scoreboard.filter_day'
                      : filter === 'Month'
                        ? 'ui.scoreboard.filter_month'
                        : 'ui.scoreboard.filter_year'
                  )}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div style={styles.roundSummaryContainer}>
            <div
              style={{
                ...styles.header,
                ...(usesCompactPurpleMobileSummary
                  ? { minHeight: '34px', marginBottom: '1px' }
                  : {}),
              }}
            >
              {usesCompactPurpleProfile ? (
                <>
                  <div style={styles.avatarContainer}>
                    {topSummaryPlayers.map((player, index) => {
                      const size = usesCompactPurpleMobileSummary
                        ? '30px'
                        : '28px';
                      const baseCircleStyle: React.CSSProperties = {
                        width: size,
                        height: size,
                        borderRadius: '50%',
                        border: '1px solid #7B3CFF',
                        marginLeft: index > 0 ? '-7px' : '0',
                        background: '#25104D',
                        zIndex: topSummaryPlayers.length - index,
                        display: 'inline-block',
                        overflow: 'hidden',
                        objectFit: 'cover',
                      };
                      const avatarUrl = getResolvedAvatarUrl(player.player);
                      if (avatarUrl) {
                        return (
                          <img
                            key={`summary-avatar-${player.player}-${index}`}
                            src={avatarUrl}
                            alt={`Top Player ${index + 1}`}
                            onError={event =>
                              applyFallbackAvatar(event, player.player)
                            }
                            style={baseCircleStyle}
                          />
                        );
                      }
                      return (
                        <span
                          key={`summary-circle-${player.player}-${index}`}
                          style={baseCircleStyle}
                        />
                      );
                    })}
                  </div>
                  <div style={styles.totalWinContainer}>
                    <span style={styles.totalWinAmount}>
                      {formatNumber(totalCashoutAmount)}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div
                    style={{
                      ...styles.totalWinContainer,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <span style={styles.totalWinAmount}>
                      {formatNumber(totalCashoutAmount)}
                    </span>
                    {(isSkySniffer || usesCompactPurpleProfile) && (
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6 3H18V9C18 12.3137 15.3137 15 12 15C8.68629 15 6 12.3137 6 9V3Z"
                          stroke="#E5A100"
                          strokeWidth="1.5"
                          fill="none"
                        />
                        <path
                          d="M6 5H4C2.89543 5 2 4.10457 2 3V3C2 3 2.89543 3 4 3H6"
                          stroke="#E5A100"
                          strokeWidth="1.5"
                          fill="none"
                        />
                        <path
                          d="M18 5H20C21.1046 5 22 4.10457 22 3V3C22 3 21.1046 3 20 3H18"
                          stroke="#E5A100"
                          strokeWidth="1.5"
                          fill="none"
                        />
                        <path
                          d="M6 7H4C2.89543 7 2 7.89543 2 9V9C2 9 2.89543 9 4 9H6.08"
                          stroke="#E5A100"
                          strokeWidth="1.5"
                          fill="none"
                        />
                        <path
                          d="M18 7H20C21.1046 7 22 7.89543 22 9V9C22 9 21.1046 9 20 9H17.92"
                          stroke="#E5A100"
                          strokeWidth="1.5"
                          fill="none"
                        />
                        <line
                          x1="12"
                          y1="15"
                          x2="12"
                          y2="18"
                          stroke="#E5A100"
                          strokeWidth="1.5"
                        />
                        <rect
                          x="8"
                          y="18"
                          width="8"
                          height="2.5"
                          rx="1"
                          stroke="#E5A100"
                          strokeWidth="1.5"
                          fill="none"
                        />
                      </svg>
                    )}
                  </div>
                  <div style={styles.avatarContainer}>
                    {assetManager &&
                      topSummaryPlayers.length > 0 &&
                      topSummaryPlayers.map((player, index) => {
                        const avatarUrl = getResolvedAvatarUrl(player.player);
                        return (
                          <img
                            key={player.player + index}
                            src={avatarUrl}
                            alt={`Top Player ${index + 1}`}
                            onError={event =>
                              applyFallbackAvatar(event, player.player)
                            }
                            style={{
                              ...styles.avatarImage,
                              marginLeft: index > 0 ? '-8px' : '0',
                              zIndex: topSummaryPlayers.length - index,
                            }}
                          />
                        );
                      })}
                  </div>
                </>
              )}
            </div>
            <div
              style={{
                ...styles.stats,
                ...(usesCompactPurpleMobileSummary
                  ? { marginTop: '4px', alignItems: 'flex-end' }
                  : {}),
              }}
            >
              {usesCompactPurpleProfile ? (
                <>
                  <div style={styles.betCountText}>
                    {renderBetCountDisplay(activeBetsCount, totalBetsCount)}
                    <span style={styles.betLabel}>
                      {t('ui.game_info.bets')}
                    </span>
                  </div>
                  <div style={styles.totalWinLabel}>
                    {t('ui.scoreboard.total_win_currency', {
                      currency: formatCurrencyUnit(currency),
                    })}
                  </div>
                </>
              ) : (
                <>
                  <div style={styles.totalWinLabel}>
                    {t('ui.scoreboard.total_win_currency', {
                      currency: formatCurrencyUnit(currency),
                    })}
                  </div>
                  <div style={styles.betCountText}>
                    {renderBetCountDisplay(activeBetsCount, totalBetsCount)}
                    <span style={styles.betLabel}>
                      {t('ui.game_info.bets')}
                    </span>
                  </div>
                </>
              )}
            </div>
            <div style={styles.progress}>
              <div
                style={{
                  ...styles.roundProgressBar,
                  width: `${progressBarWidth}%`,
                }}
              ></div>
            </div>
          </div>
        )}

        {/* --- BET LIST CONTAINER --- */}
        <div
          style={{
            ...styles.betListContainer,
            position: 'relative',
            overflow: 'hidden',
            flex: 1,
          }}
        >
          {activeTab !== 'top' && (
            <div style={styles.betListHeader}>
              <span
                style={
                  usesCompactPurpleProfile
                    ? styles.betListHeaderPlayer
                    : {
                        ...styles.betListHeaderItem,
                        minWidth: '92px',
                        marginLeft: '0',
                        textAlign: 'left',
                      }
                }
              >
                {' '}
                {t('ui.game_info.player')}{' '}
              </span>
              <span style={styles.betListHeaderItem}>
                {t('ui.control_panel.bet_panel.bet_currency', {
                  currency: formatCurrencyUnit(currency),
                })}
              </span>
              <span
                style={{
                  ...styles.betListHeaderItem,
                  color: usesCompactPurpleProfile ? '#B347FF' : undefined,
                }}
              >
                {isSkyxNew
                  ? activeTab === 'all'
                    ? t('ui.scoreboard.column_collect_x')
                    : t('ui.scoreboard.column_multiplier')
                  : 'X'}
              </span>
              <span
                style={{
                  ...styles.betListHeaderItem,
                  color: usesCompactPurpleProfile ? '#B347FF' : undefined,
                }}
              >
                {t('ui.hud.win_currency', {
                  currency: formatCurrencyUnit(currency),
                })}
              </span>
            </div>
          )}

          {/* --- SPECIFIC HEADER FOR TOP ROUNDS --- */}
          {activeTab === 'top' && topFilter === 'Rounds' && (
            <div
              style={{
                ...styles.betListHeader,
                ...(usesCompactPurpleProfile
                  ? {
                      padding: '8px 10px',
                      marginTop: '4px',
                      marginBottom: '2px',
                    }
                  : {}),
              }}
            >
              <span
                style={{
                  ...styles.betListHeaderItem,
                  textAlign: 'left',
                  flex: 3, // Matches Date cell Flex (3)
                  marginLeft: usesCompactPurpleProfile ? '3px' : '12px',
                  padding: usesCompactPurpleProfile ? '0' : undefined,
                  color: usesCompactPurpleProfile
                    ? 'rgba(201,184,245,0.7)'
                    : '#7b7b7b',
                  fontWeight: 'normal',
                  minWidth: '0',
                }}
              >
                {t('ui.scoreboard.date_and_time')}
              </span>

              <span
                style={{
                  ...styles.betListHeaderItem,
                  textAlign: 'center',
                  minWidth: 'auto',
                  width: '80px', // Matches Multiplier cell Width
                  marginLeft: usesCompactPurpleProfile ? '3px' : '4px',
                  marginRight: usesCompactPurpleProfile ? '10px' : undefined,
                  padding: usesCompactPurpleProfile ? '0' : undefined,
                  color: usesCompactPurpleProfile ? '#B347FF' : '#7b7b7b',
                  fontWeight: 'normal',
                  flexShrink: 0,
                }}
              >
                {isSkyxNew ? t('ui.scoreboard.column_multiplier') : 'X'}
              </span>

              <span
                style={{
                  flex: 2, // Matches Right Container Flex (2)
                  minWidth: '0',
                }}
              ></span>
            </div>
          )}

          <div
            ref={betListRef}
            style={{ ...styles.betList, flex: 1, overflowY: 'auto' }}
            className="bet-list-scrollbar"
          >
            {(isLoadingLeaderboard && activeTab === 'top') ||
            (isLoadingPrevious &&
              activeTab === 'previous' &&
              !hasPreviousRoundCache) ? (
              // Red Circular Loading Indicator
              <div style={styles.loaderContainer}>
                <div className="spinner" style={styles.circularLoader}></div>
              </div>
            ) : betsToShow.length ? (
              activeTab === 'top' ? (
                (betsToShow as TopPlayerStat[]).map(renderTopBetRow)
              ) : (
                (betsToShow as PlayerBet[]).map(renderBetRow)
              )
            ) : usesCompactPurpleProfile ? (
              <div style={styles.noBetsContainer}>
                <svg
                  width="36"
                  height="36"
                  viewBox="0 0 24 24"
                  fill="none"
                  style={styles.noBetsIcon}
                >
                  <rect
                    x="4"
                    y="3"
                    width="16"
                    height="18"
                    rx="3"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M8 8h8M8 12h8M8 16h5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                <span>{t('ui.game_info.no_bets_yet')}</span>
              </div>
            ) : (
              <div style={styles.noBetsNotice}>
                {t('ui.game_info.no_bets_yet')}
              </div>
            )}
          </div>
        </div>

        {/* --- FOOTER: OVERLAYS CONTENT --- */}
        <div
          style={styles.scoreBoardFooter}
          className={
            usesCompactPurpleProfile ? 'skyx-scoreboard-footer' : undefined
          }
        >
          <div
            style={{
              ...styles.provablyFairContainer,
              ...(isProvablyFairActive ? styles.provablyFairActive : {}),
            }}
            onClick={handleProvablyFairClick}
            tabIndex={0}
            role="button"
          >
            <img
              src={provablyFairIconUrl}
              alt="Provably Fair Icon"
              style={styles.provablyFairIcon}
            />
            <span
              style={{
                ...styles.provablyFairText,
                ...(isProvablyFairActive ? styles.provablyFairActive : {}),
              }}
            >
              {t('ui.popuppanel.provably_fair.game')}
            </span>
          </div>
          <div style={styles.poweredByPlayAIContainer}>
            <span style={styles.scoreBoardFooterText}>
              {t('ui.scoreboard.powered_by')}{' '}
            </span>
            <span
              style={{ ...styles.scoreBoardFooterLink, cursor: 'pointer' }}
              onClick={() =>
                window.open('https://portal.play-studios.ai/', '_blank')
              }
            >
              {isSkyxNew
                ? t('ui.scoreboard.powered_by_brand', {
                    defaultValue: 'PLAY-STUDIOS.AI',
                  })
                : 'PLAY.AI'}
            </span>
          </div>
        </div>
      </div>

      <ProvablyFairInfoModal
        isOpen={isModalOpen}
        onClose={closeModal}
        assetManager={assetManager}
        styles={provablyFairInfoStyles}
        i18nGameId={platformUIConfig.provablyFairI18nGameId}
        headerIconUrl={pfInfoHeaderIconUrl}
        diagramOverride={platformUIConfig.renderProvablyFairInfoDiagram?.({
          assetManager,
        })}
      />
    </div>
  );
};
